import { useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import logger from "../lib/logger";
import LoginHistory from "./LoginHistory";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus, Loader2, ShieldCheck, Mail, XCircle, MonitorSmartphone, LogOut } from "lucide-react";

export default function AdminsManager() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [adminsR, invitesR, devicesR] = await Promise.all([
        api.get("/admins"),
        api.get("/admins/invites"),
        api.get("/admins/devices"),
      ]);
      setAdmins(adminsR.data || []);
      setInvites(invitesR.data || []);
      setDevices(devicesR.data || []);
    } catch (err) {
      logger.error("Failed to load admins:", err);
      toast.error("Failed to load administrators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteAdmin = async (admin) => {
    if (admin.is_self) return;
    if (!window.confirm(`Remove administrator ${admin.email}?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admins/${admin.id}`);
      toast.success(`Removed ${admin.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove administrator");
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admins-tab">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500">Access control</div>
          <h2 className="text-2xl font-black tracking-tight mt-1">Administrators</h2>
          <p className="text-sm text-gray-600 mt-2 max-w-xl">
            People here can sign in with administrator privileges — full access to schedule, sites,
            team members, attendance, SLA tools, and reports. Add new admins below or rotate a
            password without making the user reset it themselves.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowInvite(true)}
            data-testid="invite-admin-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] rounded-sm transition"
          >
            <Mail className="w-4 h-4" /> Invite by email
          </button>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-admin-button"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#002FA7] text-[#002FA7] font-bold tracking-wide hover:bg-[#002FA7] hover:text-white rounded-sm transition"
          >
            <UserPlus className="w-4 h-4" /> Add manually
          </button>
        </div>
      </header>

      {invites.length > 0 && (
        <section className="border border-amber-300 bg-amber-50 rounded-sm p-4" data-testid="pending-invites-section">
          <h4 className="text-sm font-black tracking-tight mb-2">Pending invitations ({invites.length})</h4>
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`invite-row-${inv.id}`}>
              <Mail className="w-3.5 h-3.5 text-amber-700" />
              <span className="flex-1 font-medium">{inv.email}</span>
              <span className="text-xs text-gray-500">
                expires {inv.expires_at ? inv.expires_at.split("T")[0] : "—"}
              </span>
              <button
                onClick={async () => {
                  try {
                    await api.delete(`/admins/invites/${inv.id}`);
                    toast.success(`Invitation to ${inv.email} revoked`);
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed to revoke");
                  }
                }}
                data-testid={`revoke-invite-${inv.id}`}
                className="inline-flex items-center gap-1 px-2 py-1 border border-red-300 text-red-700 text-xs font-bold hover:bg-red-50 rounded-sm"
              >
                <XCircle className="w-3 h-3" /> Revoke
              </button>
            </div>
          ))}
        </section>
      )}

      <section className="border border-gray-300 bg-white rounded-sm p-4" data-testid="active-devices-section">
        <div className="flex items-center gap-2 mb-1">
          <MonitorSmartphone className="w-4 h-4 text-[#002FA7]" />
          <h4 className="text-sm font-black tracking-tight">Active devices ({devices.length})</h4>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Every device currently signed in to an administrator account. Sessions expire after 12 hours.
        </p>
        {devices.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No active admin sessions.</p>
        ) : (
          devices.map((d) => (
            <div key={d.sid} className="flex items-center gap-3 py-2 border-t border-gray-100 text-sm flex-wrap" data-testid={`device-row-${d.sid}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold">
                  {d.device_label}
                  {d.current && (
                    <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] font-bold uppercase tracking-wider rounded-sm">
                      This device
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">{d.admin_name} · {d.admin_email}</div>
              </div>
              <span className="text-xs text-gray-500">
                signed in {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
              </span>
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.delete(`/admins/devices/${d.sid}`);
                    if (data.signed_out_self) {
                      window.location.href = "/";
                      return;
                    }
                    toast.success(`Signed out ${d.device_label} (${d.admin_name})`);
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed to sign out device");
                  }
                }}
                data-testid={`signout-device-${d.sid}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-red-300 text-red-700 text-xs font-bold hover:bg-red-50 rounded-sm"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          ))
        )}
      </section>

      <LoginHistory />

      <section className="border border-gray-300 bg-white rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-3 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading administrators…
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admins-table">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300 text-left">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b border-gray-200 last:border-0" data-testid={`admin-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2">
                    {a.name || "—"}
                    {a.is_self && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7] text-white text-[10px] uppercase tracking-widest rounded-sm">
                        <ShieldCheck className="w-3 h-3" /> You
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{a.email}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {a.created_at ? a.created_at.split("T")[0] : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => setResetTarget(a)}
                        data-testid={`reset-password-${a.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-bold tracking-wide hover:bg-gray-100 rounded-sm"
                      >
                        <KeyRound className="w-3 h-3" /> Reset password
                      </button>
                      <button
                        onClick={() => deleteAdmin(a)}
                        disabled={a.is_self}
                        data-testid={`delete-admin-${a.id}`}
                        title={a.is_self ? "You can't delete your own account" : "Remove this administrator"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-700 text-xs font-bold tracking-wide hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed rounded-sm"
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="text-xs text-gray-500 leading-relaxed">
        Tip: you can also create admins from the command line with
        <code className="ml-1 px-1 py-0.5 bg-gray-100 rounded-sm font-mono">
          python /app/backend/create_admin.py --email …
        </code>
        (useful for recovery if no admin is available to log in).
      </p>

      {showAdd && (
        <AddAdminModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {showInvite && (
        <InviteAdminModal
          onClose={() => setShowInvite(false)}
          onSent={() => { setShowInvite(false); load(); }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          admin={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); }}
        />
      )}
    </div>
  );
}

function InviteAdminModal({ onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admins/invites", { email: email.trim().toLowerCase() });
      if (data.email_sent) {
        toast.success(`Invitation emailed to ${data.email}`);
      } else {
        toast.message(`Invite created — email sending is disabled, share this link manually: ${data.invite_link}`);
      }
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invite-admin-modal">
      <form onSubmit={submit} className="bg-white p-6 rounded-sm max-w-md w-full space-y-4">
        <h3 className="text-xl font-black tracking-tight">Invite administrator</h3>
        <p className="text-sm text-gray-600">
          They'll receive an email with a secure one-time link (valid 72 hours) to set their own
          name and password — no passwords shared over chat or email.
        </p>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="invite-admin-email">
            Email
          </label>
          <input
            id="invite-admin-email"
            data-testid="invite-admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm"
            autoFocus
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-invite-admin"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invitation"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="cancel-invite-admin"
            className="px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function AddAdminModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admins", { email: email.trim().toLowerCase(), name: name.trim(), password });
      toast.success(`Created administrator ${email}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create administrator");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-admin-modal">
      <form onSubmit={submit} className="bg-white p-6 rounded-sm max-w-md w-full space-y-4">
        <h3 className="text-xl font-black tracking-tight">Add administrator</h3>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="new-admin-email">
            Email
          </label>
          <input
            id="new-admin-email"
            data-testid="new-admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="new-admin-name">
            Full name
          </label>
          <input
            id="new-admin-name"
            data-testid="new-admin-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="new-admin-password">
            Password (min 8 chars)
          </label>
          <input
            id="new-admin-password"
            data-testid="new-admin-password"
            type="text"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm font-mono"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-add-admin"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create administrator"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="cancel-add-admin"
            className="px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal({ admin, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admins/${admin.id}/reset-password`, { new_password: password });
      toast.success(`Password updated for ${admin.email}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reset-password-modal">
      <form onSubmit={submit} className="bg-white p-6 rounded-sm max-w-md w-full space-y-4">
        <h3 className="text-xl font-black tracking-tight">Reset password</h3>
        <p className="text-sm text-gray-600">
          Setting a new password for <strong>{admin.email}</strong>. Make sure you share it
          with them through a secure channel — it's stored as a bcrypt hash and cannot be
          recovered later.
        </p>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="reset-admin-password">
            New password (min 8 chars)
          </label>
          <input
            id="reset-admin-password"
            data-testid="reset-admin-password"
            type="text"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm font-mono"
            autoFocus
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-reset-password"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
